import { LeafRank } from '../../../../../../../components/know-why/LeafRank'
import RenderHTML from '../../../../../../../components/ui-components/RenderHTML'
import { PowerFormBadge } from '../../../../../../../components/ui-components/PowerFormBadge'
import { RegularTag } from '../../../../../../../components/ui-components/RegularTag'
import { mfLogoBaseUrl } from '../../../../../../../env'
import type { PlanAmount } from '../../../../display-values/lumpsumPlanAmount'
import { LumpsumRecommendationActionType } from '../../../api/common.types'
import { FundKindSubtitle } from '../../../plans-list/lumpsum-ui-components/PlansFundCard'
import { RecommendationFundTileActionFooter } from './RecommendationFundTileActionFooter'
import { mapRatingToPowerForm } from '../../../../utils/mapRatingToPowerFormUtils'
import { lightImpactVibrate } from '../../../../../../../utils/appBridge'

export const RECOMMENDATION_SINGLE_FUND_CARD_WIDTH_CLASS = 'w-full'
export const RECOMMENDATION_CAROUSEL_FUND_CARD_WIDTH_CLASS = 'w-full'
/** Carousel slide width as a share of the scroll viewport (container query). */
export const RECOMMENDATION_CAROUSEL_CARD_OUTER_WIDTH = '80cqw'

const planRecommendationFundTileChevronSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="9" height="14" viewBox="0 0 9 14" fill="none">
  <path d="M7.52461 5.743L1.78461 0.283002C1.68405 0.1874 1.56564 0.112541 1.43614 0.0627014C1.30664 0.0128614 1.1686 -0.0109843 1.02989 -0.0074743C0.891174 -0.00396425 0.754511 0.0268328 0.627701 0.0831585C0.500891 0.139484 0.386417 0.220235 0.290814 0.320802C0.0977368 0.523905 -0.00675039 0.795389 0.000338469 1.07553C0.00742733 1.35567 0.125512 1.62152 0.328614 1.8146L5.52541 6.7496C5.5599 6.78218 5.58738 6.82147 5.60616 6.86504C5.62493 6.90861 5.63462 6.95556 5.63462 7.003C5.63462 7.05045 5.62493 7.09739 5.60616 7.14097C5.58738 7.18454 5.5599 7.22382 5.52541 7.2564L0.328614 12.1914C0.126625 12.3832 0.00909272 12.6473 0.00187256 12.9258C-0.00534761 13.2042 0.0983357 13.4741 0.290114 13.6761C0.481892 13.8781 0.746055 13.9956 1.02449 14.0028C1.30293 14.0101 1.57282 13.9064 1.77481 13.7146L7.53862 8.2406C7.70262 8.0762 7.83243 7.88095 7.92056 7.66611C8.00869 7.45127 8.0534 7.2211 8.0521 6.98888C8.0508 6.75667 8.00351 6.52702 7.91298 6.31318C7.82244 6.09934 7.69045 5.90555 7.52461 5.743Z" fill="#8E9393"/>
</svg>`

export type PlanRecommendationFundTileRecommendationAction = {
  action: LumpsumRecommendationActionType
  recommendationFormattedAmount: PlanAmount
  isCompleted?: boolean
  onActionClick?: () => void
}

type PlanRecommendationFundTileProps = {
  mfLogoUrl: string
  fundName: string
  fundCategory: string
  fundNature?: string
  powerRating: string
  powerRank: number | null
  purchaseMode?: string | null
  fundIsin?: string
  widthClassName: string
  heightClassName?: string
  outerClassName?: string
  recommendationAction?: PlanRecommendationFundTileRecommendationAction
  onCardClick?: () => void
}

export function PlanRecommendationFundTile({
  mfLogoUrl,
  fundName,
  fundCategory,
  fundNature = '',
  powerRating,
  powerRank,
  purchaseMode,
  fundIsin = '',
  widthClassName,
  heightClassName = 'h-[160px]',
  outerClassName = '',
  recommendationAction,
  onCardClick
}: PlanRecommendationFundTileProps) {
  const ratingPowerForm = mapRatingToPowerForm(powerRating)
  const isRegularPurchaseMode = (purchaseMode ?? '').toUpperCase() === 'REGULAR'
  const card = (
    <div
      className={`border rounded-[12px] p-2 pt-4 bg-white border-[#F0F4F7] ${heightClassName} ${widthClassName}`}
    >
      <div
        className={onCardClick ? 'flex cursor-pointer flex-row items-start gap-3' : 'flex flex-row items-start gap-3'}
        onClick={
          onCardClick
            ? (event) => {
                event.stopPropagation()
                lightImpactVibrate()
                onCardClick()
              }
            : undefined
        }
      >
        <div className="h-10 w-10 flex-shrink-0">
          <img className="h-full w-full object-contain" src={`${mfLogoBaseUrl}${mfLogoUrl}`} alt="" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-start justify-between gap-3 min-w-0">
            <div className="min-w-0 flex-1 pr-5">
              <div className="max-w-[260px] truncate text-[13px] font-[400] leading-[140%] text-[#232323]">
                {fundName}
              </div>
              <FundKindSubtitle fundNature={fundNature} category={fundCategory} />
            </div>
            <div className="pt-3 pr-1">
              <RenderHTML innerHTMLText={planRecommendationFundTileChevronSvg} />
            </div>
          </div>
          <div className="pt-3 flex items-center gap-2 flex-wrap">
            <LeafRank isElite powerRank={powerRank} powerForm={ratingPowerForm} />
            <PowerFormBadge powerForm={ratingPowerForm} />
            {isRegularPurchaseMode ? <RegularTag /> : null}
          </div>
        </div>
      </div>
      {recommendationAction ? (
        <div className="pt-5">
          <RecommendationFundTileActionFooter
            actionLabel={buildPlanRecommendationFundActionLabel(
              recommendationAction.action,
              recommendationAction.recommendationFormattedAmount
            )}
            isCompleted={recommendationAction.isCompleted ?? false}
            onActionClick={recommendationAction.onActionClick}
          />
        </div>
      ) : null}
    </div>
  )

  if (outerClassName.length > 0) {
    return <div className={outerClassName}>{card}</div>
  }

  return card
}

const LUMPSUM_RECOMMENDATION_ACTION_LABELS: Record<LumpsumRecommendationActionType, string> = {
  [LumpsumRecommendationActionType.BUY]: 'Buy',
  [LumpsumRecommendationActionType.SELL]: 'Sell'
}

export function buildPlanRecommendationFundActionLabel(
  action: LumpsumRecommendationActionType,
  recommendationFormattedAmount: PlanAmount
): string {
  return `${LUMPSUM_RECOMMENDATION_ACTION_LABELS[action]} ${recommendationFormattedAmount.label}`
}
